import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSnapshot } from "valtio";
import ReactSwitch from "react-switch";

import {
    templateActions,
    templateStore,
} from "@pages/Applications/C&C/C&CSetup/templateStore";

import { FormConfig, DEFAULT_EMPTY_MEDIA, SWITCH_PROPS } from "./constants";
import { BannerSection } from "./components/BannerSection";
import { InfoBlockSection } from "./components/InfoBlockSection";
import { FormFieldsSection } from "./components/FormFieldsSection";
import { SubmitButtonSection } from "./components/SubmitButtonSection";
import { LegalTextSection } from "./components/LegalTextSection";

interface Props {
    config: FormConfig;
}

export function FormConfigPage({ config }: Props) {
    const {
        basePath: BASE_PATH,
        defaultInfoBlock,
        defaultFormTitle,
        defaultFormSubtitle,
        defaultSubmitButton,
        defaultLegalText,
        sectionDefs,
    } = config;

    const { t } = useTranslation();
    const snap = useSnapshot(templateStore);
    const mainColor = useSelector(
        (state: any) => state.global.infoUser.maincolor
    );

    const [isReady, setIsReady] = useState(false);

    // Initialize template paths
    useEffect(() => {
        templateActions.ensurePathExists([...BASE_PATH, "Activer"], false);
        templateActions.ensurePathExists([...BASE_PATH, "Banner"], {
            Activer: true,
            Desktop: { ...DEFAULT_EMPTY_MEDIA },
            Mobile: { ...DEFAULT_EMPTY_MEDIA },
        });
        templateActions.ensurePathExists(
            [...BASE_PATH, "InfoBlock"],
            defaultInfoBlock
        );
        templateActions.ensurePathExists(
            [...BASE_PATH, "FormTitle"],
            defaultFormTitle
        );
        templateActions.ensurePathExists(
            [...BASE_PATH, "FormTitleVisible"],
            true
        );
        templateActions.ensurePathExists(
            [...BASE_PATH, "FormSubtitle"],
            defaultFormSubtitle
        );
        templateActions.ensurePathExists(
            [...BASE_PATH, "FormSubtitleVisible"],
            true
        );
        templateActions.ensurePathExists(
            [...BASE_PATH, "SubmitButton"],
            defaultSubmitButton
        );
        templateActions.ensurePathExists(
            [...BASE_PATH, "LegalText"],
            defaultLegalText
        );

        sectionDefs.forEach((section) => {
            templateActions.ensurePathExists(
                [...BASE_PATH, "Sections", section.key, "Title"],
                section.defaultTitle
            );
            section.fields.forEach((field) => {
                templateActions.ensurePathExists(
                    [
                        ...BASE_PATH,
                        "Sections",
                        section.key,
                        "Fields",
                        field.key,
                    ],
                    {
                        Label: { ...field.defaultLabel },
                        Placeholder: { ...field.defaultPlaceholder },
                        Required: field.defaultRequired,
                        Activer: true,
                        Visible: true,
                    }
                );
            });
        });

        setIsReady(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getData = useCallback(
        (subPath: string[]) => {
            try {
                return subPath.reduce(
                    (acc: any, key) => acc?.[key],
                    snap.template
                );
            } catch {
                return undefined;
            }
        },
        [snap.template]
    );

    const updatePath = useCallback((subPath: string[], value: any) => {
        templateActions.updateTemplate(["template", ...subPath], value);
    }, []);

    if (!isReady) return null;

    const formData = getData(BASE_PATH) || {};
    const formEnabled = formData?.Activer ?? true;

    return (
        <div style={{ padding: "20px", maxHeight: "70vh", overflowY: "auto" }}>
            {/* Toggle global */}
            <div
                className="d-flex align-items-center justify-content-between mb-3"
                style={{
                    padding: "12px 16px",
                    background: "#f8f9fa",
                    borderRadius: 8,
                    border: "1px solid #e0e0e0",
                }}
            >
                <span style={{ fontWeight: 600, fontSize: 16 }}>
                    {t("Enable form")}
                </span>
                <ReactSwitch
                    data-testid="form-enable-switch"
                    {...SWITCH_PROPS}
                    checked={formEnabled}
                    onChange={() =>
                        updatePath([...BASE_PATH, "Activer"], !formEnabled)
                    }
                />
            </div>

            <BannerSection
                basePath={BASE_PATH}
                bannerEnabled={formData?.Banner?.Activer ?? true}
                mainColor={mainColor}
                updatePath={updatePath}
            />

            <InfoBlockSection
                basePath={BASE_PATH}
                infoBlock={formData?.InfoBlock || defaultInfoBlock}
                defaultInfoBlock={defaultInfoBlock}
                mainColor={mainColor}
                updatePath={updatePath}
            />

            <FormFieldsSection
                basePath={BASE_PATH}
                formData={formData}
                defaultFormTitle={defaultFormTitle}
                defaultFormSubtitle={defaultFormSubtitle}
                sectionDefs={sectionDefs}
                mainColor={mainColor}
                updatePath={updatePath}
            />

            <SubmitButtonSection
                basePath={BASE_PATH}
                submitBtn={formData?.SubmitButton || defaultSubmitButton}
                defaultSubmitButton={defaultSubmitButton}
                mainColor={mainColor}
                updatePath={updatePath}
            />

            <LegalTextSection
                basePath={BASE_PATH}
                legalText={formData?.LegalText || defaultLegalText}
                defaultLegalText={defaultLegalText}
                mainColor={mainColor}
                updatePath={updatePath}
            />
        </div>
    );
}
